// ─────────────────────────────────────────────────────────────────────────────
// app/lib/api/authApi.ts
// Yazar: Bilge
//
// Kimlik doğrulama (auth) işlemlerine ait RTK Query endpoint'leri.
// baseApi.injectEndpoints() ile merkezi API'ye bağlanır.
//
// Endpoint'ler:
//   login           → POST /auth/login          – Kullanıcı girişi
//   refreshToken    → POST /auth/token/refresh  – Access token yenileme
//   logout          → POST /auth/logout         – Çıkış yapma
//   getProfile      → GET  /get-profile         – Aktif kullanıcı bilgisi
//   updateProfile   → PUT  /update-profile      – Profil güncelleme (FormData)
// ─────────────────────────────────────────────────────────────────────────────

import { baseApi } from "./baseApi";
import type { LoginParams, AuthTokens, AuthUser, UpdateProfileParams } from "@/app/(auth)/login/types";

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({

    // POST /auth/login
    // Yanıt: { status, message, data: AuthTokens }
    // transformResponse: Yalnızca data alanını alır; token'lar useLoginForm'da cookie'ye yazılır
    login: builder.mutation<AuthTokens, LoginParams>({
      query: (body) => ({ url: "/auth/login", method: "POST", body }),
      transformResponse: (res: { status: boolean; message: string; data: AuthTokens }) => res.data,
    }),

    // POST /auth/token/refresh
    // axiosBaseQuery.ts içindeki tryRefreshToken() bu endpoint'i doğrudan Axios ile çağırır.
    // Bu mutation daha çok manuel çağrı ya da test amaçlıdır.
    refreshToken: builder.mutation<AuthTokens, { refresh_token: string }>({
      query: (body) => ({ url: "/auth/token/refresh", method: "POST", body }),
      transformResponse: (res: { token_type: string; expires_in: number; access_token: string; refresh_token: string }) => ({
        token_type: res.token_type,
        expires_in: res.expires_in,
        access_token: res.access_token,
        refresh_token: res.refresh_token,
        user: {} as AuthTokens["user"],
      }),
    }),

    // POST /auth/logout
    // Sunucu tarafındaki oturumu sonlandırır.
    // Cookie temizleme işlemi clearTokens() ile client tarafında yapılır.
    logout: builder.mutation<void, void>({
      query: () => ({ url: "/auth/logout", method: "POST" }),
    }),

    // GET /get-profile
    // Giriş yapmış admin/kullanıcı bilgilerini getirir.
    // providesTags: ["Profile"] → updateProfile çalışınca otomatik yenilenir
    getProfile: builder.query<AuthUser, void>({
      query: () => ({ url: "/get-profile" }),
      transformResponse: (res: { status: boolean; data: AuthUser }) => res.data,
      providesTags: ["Profile"],
    }),

    // PUT /update-profile
    // Profil bilgilerini FormData olarak gönderir (fotoğraf yükleme desteği için)
    // invalidatesTags: ["Profile"] → güncelleme sonrası getProfile cache'i sıfırlar
    updateProfile: builder.mutation<AuthUser, UpdateProfileParams>({
      query: (params) => {
        // Tüm alanlar FormData'ya eklenir; opsiyonel alanlar boşsa atlanır
        const formData = new FormData();
        formData.append("name", params.name);
        formData.append("email", params.email);
        if (params.phone) formData.append("phone", params.phone);
        if (params.photo) formData.append("photo", params.photo);
        if (params.password) formData.append("password", params.password);
        if (params.password_confirmation) formData.append("password_confirmation", params.password_confirmation);
        return { url: "/update-profile", method: "PUT", body: formData };
      },
      transformResponse: (res: { status: boolean; message: string; data: AuthUser }) => res.data,
      invalidatesTags: ["Profile"],
    }),
  }),
  overrideExisting: true,
});

// Sayfalarda kullanılacak hook'lar — RTK Query tarafından otomatik üretilir
export const {
  useLoginMutation,
  useLogoutMutation,
  useGetProfileQuery,
  useUpdateProfileMutation,
} = authApi;